<template>
  <div class="container mt-4">
    <TopNav></TopNav>
    <div class="row">
      <div class="col-12 mt-3 mb-3">
        <h1 class="">{{ $t('SETTINGS.TITLE.CUSTOMIZE_URL') }}</h1>
      </div>
      <div class="col-md-12 p mb-3">
        {{ $t('SETTINGS.TEXT.CUSTOMIZE_URL') }}
      </div>
    </div>
    <div class="row mt-2 d-flex">
      <div class="col-5" v-if="teacher">
        <div class="d-flex flex-row flex-nowrap">
          <label class="base-url h5 mr-1">https://</label>
          <input
            :placeholder="teacher.studio_url"
            type="text"
            class="form-control mr-1"
            v-model="studioURL"
          />
          <label class="h5 base-url">{{ getDomainSuffix }}</label>
        </div>
        <div class="invalid-feedback d-block" v-show="studioURLError">
          {{ studioURLError }}
        </div>
      </div>
      <div class="col">
        <HMButton
          class="btn-primary"
          type="submit"
          :loading="inProgress"
          :disabled="!studioURL"
          v-confirm="{
            title: $t('GENERAL.LABEL.IMPORTANT'),
            text: $t('SETTINGS.TEXT.IF_YOU_CHANGE_YOUR_URL_YOUR'),
            confirm: updateStudioURL,
          }"
        >
          {{ $t('GENERAL.BUTTON.SAVE') }}
        </HMButton>
      </div>
    </div>
    <div v-if="hasCustomDomainPermission">
      <div class="row" v-if="teacher">
        <div class="col mt-4" v-if="teacher.allows_custom_domain">
          <h3>{{ $t('SETTINGS.TITLE.CUSTOM_DOMAIN_SETUP') }}</h3>
        </div>
        <div class="col mt-4" v-else>
          <h3>{{ $t('SETTINGS.TITLE.UPGRADE_FOR_CUSTOM_DOMAIN') }}</h3>
        </div>
      </div>
      <form @submit.prevent="updateCustomDomain">
        <div class="row mt-3">
          <div class="col align-self-end">
            <label for="custom-domain">{{
              $t('SETTINGS.LABEL.CUSTOM_DOMAIN')
            }}</label>
            <input
              v-if="teacher"
              type="text"
              class="form-control mr-3"
              v-model="customDomain"
              id="custom-domain"
              :disabled="domainSet || !teacher.allows_custom_domain"
            />
            <div class="invalid-feedback d-block" v-show="customDomainError">
              {{ customDomainError }}
            </div>
          </div>
          <div class="col pt-4" v-if="teacher">
            <HMButton
              class="btn-primary"
              type="submit"
              v-if="!domain && teacher.allows_custom_domain"
              :loading="domainInProgress"
            >
              {{ $t('SETTINGS.BUTTON.APPLY') }}
            </HMButton>
            <HMButton
              class="btn-primary"
              v-if="domain && teacher.allows_custom_domain"
              :loading="domainInProgress"
              @click="deleteCustomDomain"
            >
              {{ $t('SETTINGS.BUTTON.REMOVE') }}
            </HMButton>
            <HMButton
              class="btn-primary"
              v-if="!teacher.allows_custom_domain"
              @click="$router.push({ name: 'settings-adjust-plan' })"
            >
              {{ $t('SETTINGS.BUTTON.UPGRADE_ACCOUNT') }}
            </HMButton>
          </div>
        </div>
        <div class="row mt-3" v-if="domain">
          <div class="col" v-if="domain.success">
            <h5>{{ $t('SETTINGS.TEXT.SET_CNAME') }}</h5>
            <p>{{ domain.cname }}</p>
          </div>
          <div class="col" v-if="!domain.success">
            <span class="text-danger">
              {{ $t('SETTINGS.TEXT.ERROR_CUSTOM_DOMAIN') }}
            </span>
          </div>
        </div>
      </form>
      <hr class="my-4" />
      <div class="d-flex flex-row justify-content-start align-items-center">
        <span> {{ $t('SETTINGS.TEXT.USE_CUSTOM_DOMAIN_IN_EMAILS') }} </span>
        <HMSwitch
          class="ml-4"
          :value="customDomainEmails"
          @input="setCustomDomainEmails"
          :disabled="!domainSet || !isDomainValid"
          v-tooltip="{
            content: !domainSet
              ? $t('SETTINGS.TEXT.YOU_MUST_HAVE_A_DOMAIN_SET')
              : '',
            delay: 100,
            placement: 'top',
            container: 'body',
            trigger: 'click hover',
          }"
        />
      </div>
      <div
        class="d-flex flex-row justify-content-start align-items-center mt-3"
      >
        <span> {{ $t('SETTINGS.TEXT.USE_CUSTOM_DOMAIN_IN_WIDGETS') }} </span>
        <HMSwitch
          class="ml-4"
          :value="customDomainWidgets"
          @input="setCustomDomainWidgets"
          :disabled="!domainSet || !isDomainValid"
          v-tooltip="{
            content: !domainSet
              ? $t('SETTINGS.TEXT.YOU_MUST_HAVE_A_DOMAIN_SET')
              : '',
            delay: 100,
            placement: 'top',
            container: 'body',
            trigger: 'click hover',
          }"
        />
      </div>
      <div v-if="teacher.plans_campaign !== 'heymarvelous'">
        <div
          class="d-flex flex-row justify-content-start align-items-center mt-3"
        >
          <span> Switch to Marvelous </span>
          <HMSwitch
            class="ml-4"
            v-model="useMarvelous"
            @input="useMarvelousChanged"
          />
        </div>
        <div v-if="useMarvelous && !isMarvelousDomain" class="mt-2">
          <a :href="getMarvelousImpersonateUrl"
            >Click here to log into your Marvelous account</a
          >
        </div>
        <div v-else-if="!useMarvelous && isMarvelousDomain" class="mt-2">
          <a :href="getNamastreamImpersonateUrl"
            >Click here to log into your Namastream account</a
          >
        </div>
      </div>
      <hr class="my-4" />
      <PagesCustomDomain v-if="allowsCustomDomainForPages" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import TopNav from '@/web/components/navigation/TopNav.vue';
import PagesCustomDomain from '@/web/components/settings/PagesCustomDomain.vue';
import { mapGetters, mapState } from 'vuex';
import { replaceURLWithCurrentDomain } from '@/web/utils/url';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';

export default Vue.extend({
  components: {
    TopNav,
    PagesCustomDomain,
  },
  mixins: [TeacherPermissionsMixin],
  computed: {
    ...mapGetters('user', [
      'teacher',
      'accessToken',
      'customDomainEmails',
      'customDomainWidgets',
    ]),
    ...mapState('setting', ['domain']),
    hasCustomDomainPermission() {
      return this.hasPermission('has_custom_domain');
    },
    getMarvelousImpersonateUrl() {
      return `https://app.heymarvelous.com/impersonate?token=${this.accessToken}&user_type=teacher`;
    },
    getNamastreamImpersonateUrl() {
      return `https://app.namastream.com/impersonate?token=${this.accessToken}&user_type=teacher`;
    },
    isMarvelousDomain() {
      return window.location.host.toLowerCase().indexOf('heymarv') !== -1;
    },
    isDomainValid() {
      return (
        this.domain?.success &&
        this.domain?.heroku_response?.content.acm_status === 'cert issued' &&
        this.domain?.heroku_response?.content.acm_status_reason === ''
      );
    },
    getDomainSuffix() {
      if (!this.useMarvelous) {
        return '.namastream.com';
      }
      return '.heymarvelous.com';
    },
    allowsCustomDomainForPages() {
      return (
        this.hasPageBuilder &&
        (this.teacher?.allows_custom_domain ||
          this.teacher?.is_page_builder_enabled)
      );
    },
  },
  data() {
    return {
      inProgress: false,
      studioURL: null,
      studioURLError: null,
      customDomainError: null,
      customDomain: null,
      domainInProgress: false,
      domainSet: false,
      baseURL: replaceURLWithCurrentDomain(
        process.env.VUE_APP_FRONTEND_BASE_URL
      ),
      useMarvelous: false,
    };
  },
  methods: {
    async useMarvelousChanged() {
      console.log('useMarvelous=', this.useMarvelous);
      const payload = {
        frontend_url: this.useMarvelous ? 'https://app.heymarvelous.com' : null,
      };
      await this.$store.dispatch('user/updateTeacher', { teacher: payload });
    },
    async setCustomDomainEmails(value) {
      await this.$store.dispatch('user/updateTeacher', {
        teacher: {
          custom_domain_email_links_enabled: value,
        },
      });
    },
    async setCustomDomainWidgets(value) {
      await this.$store.dispatch('user/updateTeacher', {
        teacher: {
          custom_domain_widgets_enabled: value,
        },
      });
    },
    async updateStudioURL() {
      this.inProgress = true;
      const data = { teacher: { studio_url: this.studioURL } };
      try {
        this.studioURLError = null;
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.studioURLError = error.response?.data?.teacher?.studio_url?.[0];
        this.inProgress = false;
      }
      this.inProgress = false;
    },
    async updateCustomDomain() {
      this.domainInProgress = true;
      try {
        this.customDomainError = null;
        const domain_resp = await this.$store.dispatch('setting/setDomain', {
          domain: this.customDomain,
        });
        this.domainSet = true;
        if (!this.isDomainValid) {
          this.customDomainError =
            domain_resp?.heroku_response?.content?.acm_status_reason ||
            domain_resp?.heroku_response?.content?.message;
        }
      } catch (error) {
        this.customDomainError = error?.response?.data?.parameters?.domain?.[0];
      }
      this.domainInProgress = false;
    },
    async getCustomDomain() {
      try {
        const domain_resp = await this.$store.dispatch('setting/getDomain');
        this.customDomain = this.domain.domain;
        this.domainSet = true;
        if (!this.isDomainValid) {
          this.customDomainError =
            domain_resp?.heroku_response?.content?.acm_status_reason ||
            domain_resp?.heroku_response?.content?.message;
        }
      } catch (error) {
        this.customDomainError = error.response?.data;
      }
    },
    async deleteCustomDomain() {
      this.domainInProgress = true;
      try {
        await this.$store.dispatch('setting/deleteDomain');
        this.setCustomDomainEmails(false);
        this.setCustomDomainWidgets(false);
        this.customDomain = null;
        this.domainSet = false;
      } catch (error) {
        this.customDomainError = error.response?.data;
      }
      this.domainInProgress = false;
    },
  },
  mounted() {
    this.getCustomDomain();
  },
  watch: {
    teacher: {
      handler: function (teacher) {
        if (teacher) {
          this.useMarvelous = !!teacher.frontend_url;
        }
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped>
.base-url {
  padding-top: 12px;
}
</style>
